package org.btmonier.games.components

import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.browser.document
import kotlinx.browser.window
import org.btmonier.games.model.Game
import org.btmonier.games.model.SiteConfig
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

fun createGameListPage(config: SiteConfig, games: List<Game>): HTMLElement {
    // Create container
    val container = document.create.div("game-list-container") {
        div("game-list-header") {
            h1("game-list-title") { +"Game Collection" }
            if (config.subtitle != null) {
                p("game-list-subtitle") { +config.subtitle }
            }
        }
        
        // Search Bar
        div("game-search-container") {
            div("game-search-wrapper") {
                span("material-icons game-search-icon") { +"search" }
                input(InputType.text, classes = "game-search-input") {
                    id = "game-search-input"
                    placeholder = "Search games..."
                    attributes["autocomplete"] = "off"
                }
            }
        }
        
        // Results Container
        div("game-results-container") {
            id = "game-results-container"
            // Results will be populated by search
        }
    }
    
    // Setup search functionality - query elements from container
    val searchInput = container.querySelector("#game-search-input") as? HTMLInputElement
    val resultsContainer = container.querySelector("#game-results-container") as? HTMLElement
    
    fun performSearch(query: String) {
        resultsContainer?.innerHTML = ""
        
        val filteredGames = if (query.isBlank()) {
            emptyList()
        } else {
            val searchLower = query.lowercase().trim()
            val results = games.filter { game ->
                // Primary search: title (case-insensitive)
                game.metadata.title.lowercase().contains(searchLower) ||
                // Also search in tags for additional matching
                game.metadata.tags.any { tag -> tag.lowercase().contains(searchLower) }
            }
            results
        }
        
        if (filteredGames.isEmpty() && query.isNotBlank()) {
            resultsContainer?.appendChild(document.create.div("game-no-results") {
                span("material-icons") { +"search_off" }
                p { +"No games found matching \"$query\"" }
            })
        } else if (filteredGames.isNotEmpty()) {
            filteredGames.forEach { game ->
                val card = document.create.a(href = "#game-${game.metadata.slug}", classes = "game-card md-card md-card-elevated") {
                    attributes["data-slug"] = game.metadata.slug
                    
                    div("game-card-header") {
                        h2("game-card-title") { +game.metadata.title }
                        if (game.metadata.tags.isNotEmpty()) {
                            div("game-card-tags") {
                                game.metadata.tags.take(3).forEach { tag ->
                                    span("game-card-tag") { +tag }
                                }
                            }
                        }
                    }
                    
                    if (game.metadata.status != null || game.metadata.technology != null) {
                        div("game-card-metadata") {
                            game.metadata.status?.let {
                                span("game-card-status") {
                                    span("material-icons") { +"info" }
                                    +it
                                }
                            }
                            game.metadata.technology?.let {
                                span("game-card-tech") {
                                    span("material-icons") { +"code" }
                                    +it
                                }
                            }
                        }
                    }
                    
                    span("material-icons game-card-arrow") { +"arrow_forward" }
                }
                // Setup click handler for the card
                card.addEventListener("click", { e ->
                    e.preventDefault()
                    val slug = card.getAttribute("data-slug") ?: return@addEventListener
                    // Navigate to game - this will be handled by the main app
                    window.location.hash = "#game-$slug"
                })
                resultsContainer?.appendChild(card)
            }
        }
    }
    
    // Setup search input event listener
    searchInput?.addEventListener("input", { e ->
        val query = (e.target as? HTMLInputElement)?.value ?: ""
        performSearch(query)
    })
    
    // Also handle keyup for better responsiveness
    searchInput?.addEventListener("keyup", { e ->
        val query = (e.target as? HTMLInputElement)?.value ?: ""
        performSearch(query)
    })
    
    // Initial empty state - show nothing until user types
    performSearch("")
    
    return container
}
